import ReducersIndex from "reduxModules/ducks";
import { calcMaxTilesFit } from 'utilities/mathematic';

/**
* @module TutorialConfiguration
* @description Configuration module defining tutorial steps and associated actions
* for the conference room designer guided tour.
* 
* @exports {Object} tutorialActions - Action handlers for tutorial steps
* @exports {Array} tutorialSteps - Ordered array of tutorial step configurations
* 
* @description
* Tutorial Structure:
* 1. Action Handlers:
*    - setCeiling: Initializes ceiling tile configuration
*    - setTable: Sets up default table layout
*    Each handler:
*    - Checks current state
*    - Makes necessary updates
*    - Returns reset state
* 
* 2. Tutorial Steps:
*    Each step contains:
*    - content: Display text
*    - image?: Optional image path
*    - target?: Element ID(s) for spotlight
*    - menu?: Menu to activate
*    - action?: Action to trigger
* 
* @example
* // Action handler usage
* const resetState = tutorialActions.setCeiling(area, dispatch);
* 
* // Step structure
* {
*   content: "Step description",
*   image: "path/to/image.png",
*   target: "elementId",
*   menu: "menuName",
*   action: "actionName"
* }
* 
* @note
* Tutorial Flow:
* 1. Introduction
* 2. Room setup
* 3. Floor plan editing
* 4. Ceiling configuration
* 5. Furnishing placement
* 6. Acoustics setup
* 7. Microphone configuration
* 8. Speaker setup
* 9. Equipment selection
* 10. Action controls
* 11. Room management
* 12. Project actions
* 
* Action Handlers:
* - Maintain state consistency
* - Support reset functionality
* - Handle edge cases
* - Return reset data
* 
* Integration Points:
* - Uses Redux dispatch
* - Manages area state
* - Controls UI elements
* - Handles spotlight targeting
*/
const tutorialActions = {
  setCeiling: (area, dispatch) => {
    const currentCeiling = area.ceilingTiles;
    const hasCeiling = currentCeiling.tile !== 'no_ceiling_tiles';
    if (!hasCeiling) {
      // Calculate proper tile quantities dynamically based on actual room dimensions
      const { dimensions: { length, width } } = area;
      const tileSize = 0.6096; // 2'x2' tile dimensions in meters

      const maxX = calcMaxTilesFit(length, tileSize);
      const maxY = calcMaxTilesFit(width,  tileSize);
      
      dispatch(ReducersIndex.container.setCeilingTiles({ 
        tile: "2'_x_2'",
        qty: { x: maxX, y: maxY }  // Use calculated quantities to match validation
      }));
    };
    return { ceilingTiles: currentCeiling }
  },

  setTable: (area, dispatch) => {
    const currentTable = area.table;
    const hasTable = currentTable.shape;
    if (!hasTable) {
      dispatch(ReducersIndex.container.setTable({ shape: "rectangle" }));
    };
    return { table: currentTable }
  },

  setCamera: (area, dispatch) => {
    const currentCamera = area.cameras;
    const hasActiveCamera = currentCamera.hasCamera && currentCamera.model !== 'none';

    if (!hasActiveCamera) {
      // Set camera model and ensure it's active
      dispatch(ReducersIndex.container.setCameras({ model: "vidi250", hasCamera: true }));

      // Set showMap and showHeatmap synchronously to prevent flash during tutorial navigation
      // The setTimeout was causing MutationObserver to trigger after tooltip positioning
      dispatch(ReducersIndex.uiReducers.setShowMap('cameraCoverage'));
      dispatch(ReducersIndex.uiReducers.setShowHeatmap(true));
    }

    return { cameras: currentCamera }
  }
};

const tutorialSteps = [
  {
    title: "Welcome to Workplace Designer",
    content: "This is your design library where you can create, manage, and share space designs. You can start new designs, import existing ones, or select multiple designs to export and share with your team or other organizations.",
    subtitle: "Let's create your first design to get started!",
    isWelcomeScreen: true,
    showSkipGuide: true,
    buttonText: "Create design",
    target: null
  }, {
    title: "Name design",
    content: "Name your design and associate it to a specific location in Workplace. This step is optional, and you can always name or move your design to a different location later if you prefer.",
    isNameDesignScreen: true,
    showSkipGuide: true,
    buttonText: "Next",
    target: "projectName"
  }, {
    title: "Room dimensions",
    content: "Here you can define the room's length, width and ceiling height. You can also position the door anywhere along the walls. These dimensions will be used throughout the design process.",
    menu: 'floor_plan',
    target: 'floor_plan'
  }, {
    title: "Configure ceiling",
    content: "Set ceiling tile dimensions and mark any blocked tiles where equipment can't be installed. This affects where audio devices can be mounted and impacts coverage patterns.",
    menu: 'ceiling',
    target: 'ceiling',
    action: 'setCeiling'
  }, {
    title: "Add furnishing",
    content: "Add furniture to help plan accurate microphone and speaker placement. Here you choose from different table shapes and position them in your room. You can also remove chairs if needed.",
    menu: 'furnishing',
    target: 'furnishing',
    action: 'setTable'
  }, {
    title: "Room acoustics",
    content: "Select the acoustic profile that best matches your room. This setting influences equipment recommendations and coverage patterns to ensure optimal audio quality.",
    menu: 'acoustics',
    target: 'acoustics'
  }, {
    title: "Set microphones",
    content: 'Select your microphone type and coverage area. Choose "Full Room" for complete coverage or "Focused" to cover the area likely to contain people.',
    menu: 'microphones',
    target: 'microphones'
  }, {
    title: "Configure speakers",
    content: "Select whether the speakers should cover the entire room, or just the area likely to contain people.",
    menu: 'speakers',
    target: 'speakers'
  }, {
    title: "Camera setup",
    content: "Add and position camera based on your needs. Configure cameras to capture people, content areas like whiteboards, or other important zones within your space.",
    menu: 'camera',
    target: 'camera',
    action: 'setCamera'
  }, {
    title: "Equipment list",
    content: "Review all recommended equipment with quantities, models, and placement details. These lists updates automatically as you modify your design parameters.",
    menu: 'equipmentlist',
    target: 'equipment_list'
  }, {
    title: "Customize design",
    content: "These action buttons allow you to customize or analyze your design. Here you can measure distance, add, move or delete devices, check portioning details and levels and even listen to how a device would sound in your room.",
    menu: 'microphones',
    target: 'actionButtons'
  }, {
    title: "Project actions",
    content: "Click <strong>Duplicate</strong> to create a copy of this design, <strong>Export</strong> to create a PDF overview of your design parameters, <strong>Reset</strong> to start over, <strong>Request quote</strong> to have a Biamp expert contact you about your project, or <strong>Save</strong> to keep this design for future use.",
    target: ['saveButton', 'exportButton', 'designContainerQuoteButton', 'duplicateButton', 'resetButton'],
  }, {
    title: "Access designs",
    content: 'You can find all of your designs in the "My designs" section.',
    target: 'myDesignsButton',
    showSkipGuide: true,
    buttonText: "Finish guide"
  }
]

export default tutorialSteps;
export { tutorialActions };
